import React, { useState, useEffect } from 'react'
import { Box, Paper, Typography, Skeleton, useTheme, alpha, Tabs, Tab } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { analyticsApi } from '../../../../api/analyticsApi'
import FunnelChart from '../charts/FunnelChart'

const FUNNEL_KEYS = [
  { key: 'acquisition', labelKey: 'analytics_funnel_acquisition', descKey: 'analytics_funnel_acquisition_desc' },
  { key: 'registration', labelKey: 'analytics_funnel_registration', descKey: 'analytics_funnel_registration_desc' },
  { key: 'onboarding', labelKey: 'analytics_funnel_onboarding', descKey: 'analytics_funnel_onboarding_desc' },
  { key: 'conversion', labelKey: 'analytics_funnel_conversion', descKey: 'analytics_funnel_conversion_desc' },
]

const FunnelsTab = ({ period }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const goldColor = theme.palette.primary.main

  const [activeTab, setActiveTab] = useState(0)
  const [data, setData] = useState({})
  const [loading, setLoading] = useState({})

  const currentFunnel = FUNNEL_KEYS[activeTab]

  useEffect(() => {
    const fetchFunnel = async () => {
      const funnelKey = currentFunnel.key
      setLoading((prev) => ({ ...prev, [funnelKey]: true }))
      try {
        const res = await analyticsApi.getFunnel(period, funnelKey)
        setData((prev) => ({ ...prev, [funnelKey]: res.data }))
      } catch (err) {
        console.error(`Failed to fetch ${funnelKey} funnel:`, err)
      } finally {
        setLoading((prev) => ({ ...prev, [funnelKey]: false }))
      }
    }
    fetchFunnel()
  }, [period, activeTab, currentFunnel.key])

  const funnelData = data[currentFunnel.key]
  const funnelLoading = loading[currentFunnel.key] ?? true
  const steps = funnelData?.steps || funnelData || []

  return (
    <Box
      sx={{
        overflow: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
    >
      {/* Sub-tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '0.85rem',
            fontWeight: 500,
            minHeight: 36,
            px: 2,
          },
          '& .Mui-selected': {
            color: goldColor,
            fontWeight: 600,
          },
          '& .MuiTabs-indicator': {
            backgroundColor: goldColor,
            height: 2.5,
            borderRadius: '2px',
          },
        }}
      >
        {FUNNEL_KEYS.map((f) => (
          <Tab key={f.key} label={t(f.labelKey)} />
        ))}
      </Tabs>

      {/* Description */}
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '0.84rem',
          mb: 2.5,
          lineHeight: 1.6,
        }}
      >
        {t(currentFunnel.descKey)}
      </Typography>

      {/* Funnel Chart */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t(currentFunnel.labelKey)} {t('analytics_funnel_suffix')}
        </Typography>
        <FunnelChart data={steps} loading={funnelLoading} />
      </Paper>
    </Box>
  )
}

export default FunnelsTab
